/**
 * Home - SoundCloud-Inspired Discover
 *
 * Key design patterns from SoundCloud:
 * - Minimal header, no giant hero
 * - Genre/mood chips for quick filtering
 * - Horizontal scroll carousels
 * - Dense content grid
 * - Two-column layout (main + sidebar)
 * - Play buttons on hover
 * - Social proof (plays, likes)
 * - Clean, minimal card designs
 */

import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { logger } from '../utils/logger';
import streamService, { Stream } from '../services/streams';
import socialService from '../services/social';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { normalizeGenre, CANONICAL_GENRES } from '../utils/genreNormalization';
import './Home.css';

interface TopCreator {
  username: string;
  displayName: string;
  profilePictureUrl?: string;
  streamCount: number;
}

interface StreamWithStats extends Stream {
  playCount?: number;
  favoriteCount?: number;
}

const GENRES = ['All', ...CANONICAL_GENRES];
const MOODS = ['Chill', 'Energetic', 'Focus', 'Party', 'Melancholic'];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { playStream, currentStream, isPlaying } = useAudioPlayer();
  const [streams, setStreams] = useState<StreamWithStats[]>([]);
  const [loadingStreams, setLoadingStreams] = useState(true);
  const [topCreators, setTopCreators] = useState<TopCreator[]>([]);
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const carouselRef1 = useRef<HTMLDivElement>(null);
  const carouselRef2 = useRef<HTMLDivElement>(null);

  const handlePlayClick = (stream: Stream, e?: React.MouseEvent) => {
    e?.stopPropagation();
    playStream({
      streamId: stream.streamId,
      name: stream.name,
      description: stream.description,
      audioUrl: stream.audioUrl,
      coverImage: stream.coverImage,
      uploaderUsername: stream.uploaderUsername,
      uploaderDisplayName: stream.uploaderDisplayName,
      waveformData: stream.waveformData,
    });
  };

  const isStreamPlaying = (streamId: string) => {
    return currentStream?.streamId === streamId && isPlaying;
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const allStreams = await streamService.getAllStreams('active');

      // Enrich first few streams with stats
      const enrichedStreams = await Promise.all(
        allStreams.slice(0, 12).map(async (stream) => {
          try {
            const [playStats, favStats] = await Promise.all([
              socialService.getPlayStats(stream.streamId),
              socialService.getFavoriteStats(stream.streamId),
            ]);
            return {
              ...stream,
              playCount: playStats.totalPlays,
              favoriteCount: favStats.totalFavorites,
            };
          } catch {
            return stream;
          }
        })
      );

      setStreams([...enrichedStreams, ...allStreams.slice(12)]);

      // Build creators list
      const creatorMap = new Map<string, TopCreator>();
      allStreams.forEach(stream => {
        if (stream.uploaderUsername) {
          const existing = creatorMap.get(stream.uploaderUsername);
          if (existing) {
            existing.streamCount++;
          } else {
            creatorMap.set(stream.uploaderUsername, {
              username: stream.uploaderUsername,
              displayName: stream.uploaderDisplayName || stream.uploaderUsername,
              profilePictureUrl: stream.uploaderProfilePictureUrl,
              streamCount: 1,
            });
          }
        }
      });
      setTopCreators(Array.from(creatorMap.values()).sort((a, b) => b.streamCount - a.streamCount).slice(0, 6));
    } catch (error) {
      logger.error('Error loading data:', error);
    } finally {
      setLoadingStreams(false);
    }
  };

  const formatNumber = (num?: number): string => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const scroll = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const filteredStreams = selectedGenre === 'All'
    ? streams
    : streams.filter(s => normalizeGenre(s.genre) === selectedGenre);

  // Section groupings
  const trendingStreams = filteredStreams.slice(0, 8);
  const recentStreams = filteredStreams.slice(8, 16);
  const featuredStream = streams[0];

  return (
    <div className="home-v2d">
      {/* Compact Header Bar */}
      <header className="v2d-header">
        <div className="v2d-header-left">
          <h1>Discover</h1>
          <nav className="v2d-nav">
            <button className="v2d-nav-item active">Overview</button>
            <button className="v2d-nav-item" onClick={() => navigate('/streams?sort=trending')}>Trending</button>
            <button className="v2d-nav-item" onClick={() => navigate('/streams?sort=most-played')}>New</button>
            <button className="v2d-nav-item" onClick={() => navigate('/streams?sort=top-creators')}>Charts</button>
          </nav>
        </div>
        <div className="v2d-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search streams, artists, genres..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </header>

      {/* Genre Chips */}
      <section className="v2d-genres">
        <div className="v2d-genre-scroll">
          {GENRES.map((genre) => (
            <button
              key={genre}
              className={`v2d-genre-chip ${selectedGenre === genre ? 'active' : ''}`}
              onClick={() => setSelectedGenre(genre)}
            >
              {genre}
            </button>
          ))}
          <span className="v2d-divider" />
          {MOODS.map((mood) => (
            <button
              key={mood}
              className="v2d-mood-chip"
            >
              {mood}
            </button>
          ))}
        </div>
      </section>

      {/* Main Content */}
      <div className="v2d-layout">
        <main className="v2d-main">
          {/* Featured Now Playing */}
          {featuredStream && (
            <section className="v2d-featured">
              <div className="v2d-featured-inner">
                <div
                  className="v2d-featured-art"
                  onClick={() => handlePlayClick(featuredStream)}
                >
                  {featuredStream.coverImage ? (
                    <img src={featuredStream.coverImage} alt={featuredStream.name} />
                  ) : (
                    <div className="v2d-art-placeholder" />
                  )}
                  <button className="v2d-featured-play">
                    {isStreamPlaying(featuredStream.streamId) ? (
                      <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    )}
                  </button>
                </div>
                <div className="v2d-featured-info">
                  <span className="v2d-featured-label">Now Featured</span>
                  <h2
                    className="v2d-featured-title"
                    onClick={() => navigate(`/streams/${featuredStream.streamId}`)}
                  >
                    {featuredStream.name}
                  </h2>
                  {featuredStream.uploaderUsername && (
                    <Link to={`/users/${featuredStream.uploaderUsername}`} className="v2d-featured-artist">
                      {featuredStream.uploaderDisplayName || featuredStream.uploaderUsername}
                    </Link>
                  )}
                  <div className="v2d-featured-stats">
                    <span><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>{formatNumber((featuredStream as StreamWithStats).playCount)}</span>
                    <span><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>{formatNumber((featuredStream as StreamWithStats).favoriteCount)}</span>
                    <span className="v2d-genre-label">{normalizeGenre(featuredStream.genre)}</span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Trending Section */}
          <section className="v2d-section">
            <div className="v2d-section-header">
              <h2>Trending Now</h2>
              <div className="v2d-section-nav">
                <button onClick={() => scroll(carouselRef1, 'left')} className="v2d-scroll-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
                </button>
                <button onClick={() => scroll(carouselRef1, 'right')} className="v2d-scroll-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                </button>
                <Link to="/streams" className="v2d-see-all">See all</Link>
              </div>
            </div>
            <div className="v2d-carousel" ref={carouselRef1}>
              {loadingStreams ? (
                <div className="v2d-loading">Loading...</div>
              ) : (
                trendingStreams.map((stream) => (
                  <div
                    key={stream.streamId}
                    className={`v2d-track-card ${isStreamPlaying(stream.streamId) ? 'playing' : ''}`}
                    onClick={() => navigate(`/streams/${stream.streamId}`)}
                  >
                    <div className="v2d-track-art">
                      {stream.coverImage ? (
                        <img src={stream.coverImage} alt={stream.name} />
                      ) : (
                        <div className="v2d-art-placeholder" />
                      )}
                      <button
                        className="v2d-track-play"
                        onClick={(e) => handlePlayClick(stream, e)}
                      >
                        {isStreamPlaying(stream.streamId) ? (
                          <svg viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                        )}
                      </button>
                      {isStreamPlaying(stream.streamId) && (
                        <div className="v2d-now-playing-indicator">
                          <span /><span /><span />
                        </div>
                      )}
                    </div>
                    <div className="v2d-track-info">
                      <h3>{stream.name}</h3>
                      <span className="v2d-track-artist">
                        {stream.uploaderDisplayName || stream.uploaderUsername || normalizeGenre(stream.genre)}
                      </span>
                      <div className="v2d-track-stats">
                        <span>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          {formatNumber((stream as StreamWithStats).playCount)}
                        </span>
                        <span>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                          {formatNumber((stream as StreamWithStats).favoriteCount)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* New & Hot Section */}
          <section className="v2d-section">
            <div className="v2d-section-header">
              <h2>New & Hot</h2>
              <div className="v2d-section-nav">
                <button onClick={() => scroll(carouselRef2, 'left')} className="v2d-scroll-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
                </button>
                <button onClick={() => scroll(carouselRef2, 'right')} className="v2d-scroll-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                </button>
                <Link to="/streams?sort=newest" className="v2d-see-all">See all</Link>
              </div>
            </div>
            <div className="v2d-carousel" ref={carouselRef2}>
              {recentStreams.map((stream) => (
                <div
                  key={stream.streamId}
                  className={`v2d-track-card ${isStreamPlaying(stream.streamId) ? 'playing' : ''}`}
                  onClick={() => navigate(`/streams/${stream.streamId}`)}
                >
                  <div className="v2d-track-art">
                    {stream.coverImage ? (
                      <img src={stream.coverImage} alt={stream.name} />
                    ) : (
                      <div className="v2d-art-placeholder" />
                    )}
                    <button
                      className="v2d-track-play"
                      onClick={(e) => handlePlayClick(stream, e)}
                    >
                      <svg viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                    </button>
                  </div>
                  <div className="v2d-track-info">
                    <h3>{stream.name}</h3>
                    <span className="v2d-track-artist">
                      {stream.uploaderDisplayName || stream.uploaderUsername || normalizeGenre(stream.genre)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Mixed Grid Section */}
          <section className="v2d-section">
            <div className="v2d-section-header">
              <h2>Explore</h2>
            </div>
            <div className="v2d-grid">
              {filteredStreams.slice(0, 12).map((stream) => (
                <div
                  key={stream.streamId}
                  className={`v2d-grid-card ${isStreamPlaying(stream.streamId) ? 'playing' : ''}`}
                  onClick={() => navigate(`/streams/${stream.streamId}`)}
                >
                  <div className="v2d-grid-art">
                    {stream.coverImage ? (
                      <img src={stream.coverImage} alt={stream.name} />
                    ) : (
                      <div className="v2d-art-placeholder" />
                    )}
                    <button
                      className="v2d-track-play"
                      onClick={(e) => handlePlayClick(stream, e)}
                    >
                      <svg viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                    </button>
                  </div>
                  <div className="v2d-grid-info">
                    <h3>{stream.name}</h3>
                    <span>{stream.uploaderDisplayName || normalizeGenre(stream.genre)}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* Sidebar */}
        <aside className="v2d-sidebar">
          {/* Top Creators */}
          <section className="v2d-sidebar-section">
            <h3>Top Creators</h3>
            <div className="v2d-creators">
              {topCreators.map((creator, index) => (
                <Link
                  key={creator.username}
                  to={`/users/${creator.username}`}
                  className="v2d-creator"
                >
                  <span className="v2d-creator-rank">{index + 1}</span>
                  <div className="v2d-creator-avatar">
                    {creator.profilePictureUrl ? (
                      <img src={creator.profilePictureUrl} alt={creator.displayName} />
                    ) : (
                      <span>{creator.displayName.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="v2d-creator-info">
                    <h4>{creator.displayName}</h4>
                    <span>{creator.streamCount} streams</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Genres */}
          <section className="v2d-sidebar-section">
            <h3>Browse by Genre</h3>
            <div className="v2d-genre-list">
              {GENRES.filter(g => g !== 'All').map((genre) => (
                <button
                  key={genre}
                  className="v2d-genre-row"
                  onClick={() => {
                    setSelectedGenre(genre);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <span className="v2d-genre-icon" />
                  <span>{genre}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                </button>
              ))}
            </div>
          </section>

          {/* Quick Stats */}
          <section className="v2d-sidebar-section v2d-stats-card">
            <div className="v2d-stat-row">
              <span className="v2d-stat-value">{streams.length}</span>
              <span className="v2d-stat-label">Total Streams</span>
            </div>
            <div className="v2d-stat-row">
              <span className="v2d-stat-value">{topCreators.length}</span>
              <span className="v2d-stat-label">Active Creators</span>
            </div>
          </section>
        </aside>
      </div>

    </div>
  );
};

export default Home;
